import { createContext, useContext, useState, ReactNode, ComponentProps } from "react"
import Modal from "~/components/ui/Modal"

type GenericModalProps = ComponentProps<typeof Modal>

type ModalConfig = {
  generic: Omit<GenericModalProps, "open"> & { children: ReactNode }
}

type ModalType = keyof ModalConfig

interface ModalState {
  type: ModalType | null
  props: ModalConfig[keyof ModalConfig] | Record<string, never>
}

type ModalContextValue = {
  activeModal: ModalState
  openModal: <T extends ModalType>(type: T, props: ModalConfig[T]) => void
  closeModal: () => void
}

const ModalContext = createContext<ModalContextValue>({
  activeModal: { type: null, props: {} },
  openModal: () => {},
  closeModal: () => {},
})

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [activeModal, setActiveModal] = useState<ModalState>({ type: null, props: {} })

  const openModal = <T extends ModalType>(type: T, props: ModalConfig[T]) => {
    setActiveModal({ type, props })
  }

  const closeModal = () => {
    setActiveModal({ type: null, props: {} })
  }

  const renderModal = () => {
    if (!activeModal.type) return null

    const commonProps = {
      open: true,
      onClose: () => {
        closeModal()
        // @ts-ignore - we know props.onClose exists for these modals
        activeModal.props.onClose?.()
      },
    }
    switch (activeModal.type) {
      case "generic": {
        const { children, ...genericProps } = activeModal.props as ModalConfig["generic"]
        return (
          <Modal {...genericProps} {...commonProps}>
            {children}
          </Modal>
        )
      }

      default:
        return null
    }
  }

  return (
    <ModalContext.Provider value={{ activeModal, openModal, closeModal }}>
      {children}
      {activeModal.type && renderModal()}
    </ModalContext.Provider>
  )
}

export const useModal = () => useContext(ModalContext)
